import { useCallback, useState } from 'react';
import useDidUpdate from 'hooks/useDidUpdate';
import { ApiHookType } from 'api/hooks/hook.types';
import { TAuthConverter, TConverter } from 'data/types';
import { useAppDispatch } from 'redux/store';
import { AnyAction, CombinedState, ThunkAction } from '@reduxjs/toolkit';

// D - dependency type, FD, FD1- ui data type, BD, BD1 -backand data type
// 2 types of each data type in case if we give to backend some data but gets different one

const useApiResult = <FD, BD, FD1, BD1>(
  service: (dependency: FD) => Promise<BD1>,
  converter: TAuthConverter<FD, BD, FD1, BD1> | TConverter<FD1, BD1>,
  onSuccess?: (result: FD1) => void,
  onError?: (error: Error) => void,
  action?: (
    payload: FD1,
  ) =>
    | AnyAction
    | ThunkAction<
        unknown,
        CombinedState<{ [x: string]: never }>,
        undefined,
        AnyAction
      >,
): ApiHookType<FD, FD1> => {
  const [data, setData] = useState<FD1 | null | undefined>(undefined);
  const [error, setError] = useState<Error | null>(null);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dependency, setDependency] = useState<FD>();
  const refetch = useCallback(dep => {
    setDependency(dep);
  }, []);
  const dispatch = useAppDispatch();
  useDidUpdate(() => {
    if (dependency) {
      (async (): Promise<void> => {
        try {
          setIsLoading(true);
          const result: BD1 = await service(dependency);
          const convertedData: FD1 = converter.fromDb(result);
          if (action && convertedData) {
            dispatch(action(convertedData));
          }
          setData(convertedData);
          setError(null);
          setIsSuccess(true);
          if (onSuccess) {
            onSuccess(convertedData);
          }
        } catch (err) {
          const errorObj = err as Error;
          setError(errorObj);
          setData(null);
          setIsSuccess(false);
          if (onError) {
            onError(errorObj);
          }
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [dependency]);
  return { data, error, isLoading, refetch, isSuccess };
};

export default useApiResult;
